import { expireSession, renewUser, userManager } from "../auth";
import { recordApiDiagnostic } from "../terminalDiagnostics";

export class ApiError extends Error {
  constructor(
    public readonly code: "AUTHENTICATION_REQUIRED" | "SESSION_EXPIRED" | "API_ERROR",
    public readonly status?: number,
    public readonly detail?: string
  ) {
    super(detail ?? (status ? `${code}_${status}` : code));
  }
}

export function isAuthenticationError(error: unknown): boolean {
  return error instanceof ApiError &&
    (error.code === "AUTHENTICATION_REQUIRED" || error.code === "SESSION_EXPIRED");
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  locale: string;
  timezone: string;
  permissions: string[];
  lastAuthenticatedAt: string;
}

export async function loadCurrentUser(): Promise<CurrentUser> {
  return authenticatedFetch<CurrentUser>("/api/v1/me");
}

export async function authenticatedFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let user = await userManager.getUser();
  if (user?.expired) {
    user = await renewUser();
  }
  if (!user?.access_token) {
    await expireSession();
    throw new ApiError(
      user === null ? "SESSION_EXPIRED" : "AUTHENTICATION_REQUIRED"
    );
  }

  const execute = async (accessToken: string) => {
    try {
      return await fetch(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...init.headers,
        Authorization: `Bearer ${accessToken}`
      }
      });
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Erreur réseau";
      recordApiDiagnostic(path, 0, detail);
      throw error;
    }
  };

  let response = await execute(user.access_token);
  if (response.status === 401) {
    const renewedUser = await renewUser(user.access_token);
    if (!renewedUser?.access_token) {
      throw new ApiError("SESSION_EXPIRED", 401);
    }
    response = await execute(renewedUser.access_token);
  }

  if (response.status === 401) {
    await expireSession();
    throw new ApiError("SESSION_EXPIRED", 401);
  }

  if (!response.ok) {
    let detail: string | undefined;
    try {
      const problem = await response.json() as {
        detail?: string; message?: string; correlationId?: string
      };
      detail = problem.detail ?? problem.message;
      if (problem.correlationId) {
        detail = `${detail ?? "Erreur API"} · corrélation ${problem.correlationId}`;
      }
    } catch {
      // The HTTP status remains available when the response has no JSON body.
    }
    recordApiDiagnostic(path, response.status, detail);
    throw new ApiError("API_ERROR", response.status, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    const payload = await response.text();
    return JSON.parse(payload) as T;
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Réponse JSON illisible";
    throw new ApiError(
      "API_ERROR", response.status,
      `La réponse de ${path} n’a pas pu être lue : ${reason}`
    );
  }
}

export async function authenticatedBlobFetch(path: string): Promise<Blob> {
  let user = await userManager.getUser();
  if (user?.expired) user = await renewUser();
  if (!user?.access_token) {
    await expireSession();
    throw new ApiError(user === null ? "SESSION_EXPIRED" : "AUTHENTICATION_REQUIRED");
  }
  const execute = (accessToken: string) => fetch(path, {
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  let response = await execute(user.access_token);
  if (response.status === 401) {
    const renewedUser = await renewUser(user.access_token);
    if (!renewedUser?.access_token) throw new ApiError("SESSION_EXPIRED", 401);
    response = await execute(renewedUser.access_token);
  }
  if (response.status === 401) {
    await expireSession();
    throw new ApiError("SESSION_EXPIRED", 401);
  }

  if (!response.ok) {
    throw new ApiError("API_ERROR", response.status, "Document PDF inaccessible.");
  }
  return response.blob();
}
